import React from 'react';
import {
  Campaign,
  CampaignDelivery,
  CampaignDeliveryStepStatus,
  CampaignDeliveryTarget,
  CampaignEnrollment,
  CampaignWorkshopMeeting,
} from '../../../../../support/campaigns';
import {
  useCampaignCompletion,
  useCampaignStepStatusRollup,
  useFetchCampaignSteps,
  useFetchEnrollments,
} from '../../../../../hooks/campaign-hooks';
import { MetricRow } from '../../../../../support/campaignAnalytics';
import { formatPipelineUsd } from '../../../../../support/campaignLeader';
import {
  CompletionCohort,
  SalesLevelDrill,
  SalesLevelIndex,
  SalesLevelPair,
  cohortPairs,
  cohortSalesLevel,
  cohortsInDrill,
  completionCounts,
  completionCountsFromCohorts,
  deliveriesInDrill,
  deliverySalesLevel,
  deliverySequence,
  enrollmentSalesLevel,
  enrollmentsInDrill,
  farthestProgressByCohort,
  groupKeyFor,
  workshopPipelineRows,
  twoRingCompletion,
  twoRingCompletionFromCohorts,
} from '../../../../../support/campaignSalesLevels';
import {
  CampaignChartSlug,
  TileAppearance,
  useCampaignTileAppearance,
} from '../../../../../support/campaignTileAppearance';
import { StatusPill } from '../../builder/primitives';
import { toneForStatus } from '../statusTone';
import DeliveryStepStatusLoader from '../DeliveryStepStatusLoader';
import TwoRingDonut from '../TwoRingDonut';
import SalesLevelDrillSelect from '../SalesLevelDrillSelect';
import {
  ChartCard,
  Column,
  KpiStrip,
  MasterTable,
  Panel,
  dashboardCss as css,
} from '../dashboardParts';

/**
 * A single campaign, redesigned around Cisco sales-territory reporting. A Global → Sales Level 1 →
 * Sales Level 2 drill scopes every surface at once:
 *  - **Enablement completion (Req 1):** In-Progress / Completed KPIs plus a two-ring donut (inner =
 *    completion status, outer = the SL1/SL2 cohort split) and an aggregate-by-cohort table.
 *  - **Delivery progression (Req 2):** a funnel of deliveries by their farthest-reached step (hidden
 *    steps excluded).
 *  - **Workshop schedule (Req 3):** a distribution of Webex-derived schedule status (Not Scheduled /
 *    Scheduled / In-Progress / Complete, all UTC).
 *  - **Pipeline by status (Req 5):** delivery counts + attributed $ per delivery status, including
 *    sessions with no opportunity attached.
 * Per **Req 4** this global (Admin) page shows **no individual data** — every surface aggregates to
 * SL1/SL2 cohorts (who is progressing, how many, how far, and the pipeline it builds). There is
 * deliberately **no per-delivery master table** here: at thousands of sessions a flat list is unusable
 * and would expose individual rows. Individual delivery/learner detail lives only on the chain-scoped,
 * front-line leader pages, where a manager owns the individualized conversation.
 *
 * Step statuses across all deliveries come from the Go rollup endpoint in one request when served
 * (dev/uat); where it is not (prod on Django, or mid-rollout) the view falls back to fanning out one
 * headless loader per delivery, which assembles the identical array.
 */
const CampaignView: React.FC<{
  campaign: Campaign;
  deliveries: CampaignDelivery[];
  targetsByDelivery: Map<number, CampaignDeliveryTarget[]>;
  amountByOpp: Map<string, number>;
  userIndex: SalesLevelIndex;
  meetingsByDelivery: Map<number, CampaignWorkshopMeeting[]>;
}> = ({
  campaign,
  deliveries,
  targetsByDelivery,
  amountByOpp,
  userIndex,
  meetingsByDelivery,
}) => {
  // Enablement completion comes from the server-side cohort aggregation (scales past the list-API page
  // cap — up to ~10k enrollments). by=territory is the whole-org aggregate this global Admin page shows
  // (no individual data — Req 4). Where the Go route is not served (prod on Django, or mid-rollout) it
  // errors, and we fall back to fetching the enrollment rows and rolling them up client-side — so the
  // enrollment fetch is gated to ONLY the fallback path and never runs the 10k-row read otherwise.
  const completion = useCampaignCompletion(campaign.id, { by: 'territory' });
  const useCohorts = !completion.isError;
  const cohorts = React.useMemo<CompletionCohort[]>(() => completion.data?.cohorts ?? [], [completion.data]);

  const { data: enrollments } = useFetchEnrollments(campaign.id, { enabled: completion.isError });
  const { data: steps } = useFetchCampaignSteps(campaign.id);

  // Prefer the single-request Go rollup; only fan out per delivery when it is not served here.
  const rollup = useCampaignStepStatusRollup(campaign.id);
  const useFanout = rollup.isError;

  const [statusesState, setStatusesState] = React.useState<
    Record<number, CampaignDeliveryStepStatus[]>
  >({});
  const handleLoad = React.useCallback((deliveryId: number, rows: CampaignDeliveryStepStatus[]) => {
    setStatusesState((prev) => (prev[deliveryId] === rows ? prev : { ...prev, [deliveryId]: rows }));
  }, []);
  const fanoutStatuses = React.useMemo(() => Object.values(statusesState).flat(), [statusesState]);
  const stepStatuses = React.useMemo(
    () => (useFanout ? fanoutStatuses : rollup.data ?? []),
    [useFanout, fanoutStatuses, rollup.data]
  );

  const enrollmentList = React.useMemo(() => enrollments ?? [], [enrollments]);
  const stepList = React.useMemo(() => steps ?? [], [steps]);

  // Sales-level drill (Global → SL1 → SL2 → SL3). Every surface below reads this.
  const [drill, setDrill] = React.useState<SalesLevelDrill>({});
  const groupLabel =
    drill.sl1 == null ? 'Sales Level 1' : drill.sl2 == null ? 'Sales Level 2' : 'Sales Level 3';

  // Territory pairs present across both populations — drives the drill selects. The enablement side
  // comes from the server cohorts when served, else from the fetched enrollment rows (fallback).
  const drillPairs = React.useMemo<SalesLevelPair[]>(() => {
    const pairs: SalesLevelPair[] = [];
    if (useCohorts) {
      for (const p of cohortPairs(cohorts)) pairs.push(p);
    } else {
      for (const e of enrollmentList) pairs.push(enrollmentSalesLevel(userIndex, e));
    }
    for (const d of deliveries) pairs.push(deliverySalesLevel(userIndex, d));
    return pairs;
  }, [useCohorts, cohorts, enrollmentList, deliveries, userIndex]);

  // Step statuses grouped per delivery — shared by health + farthest-progress.
  const statusesByDelivery = React.useMemo(() => {
    const m = new Map<number, CampaignDeliveryStepStatus[]>();
    for (const s of stepStatuses) {
      const list = m.get(s.delivery) ?? [];
      list.push(s);
      m.set(s.delivery, list);
    }
    return m;
  }, [stepStatuses]);

  // --- Req 1: enablement completion, scoped to the drill (server cohorts, else fallback rollup) ---
  const scopedEnrollments = React.useMemo(
    () => enrollmentsInDrill(enrollmentList, userIndex, drill),
    [enrollmentList, userIndex, drill]
  );
  const enablementCounts = React.useMemo(
    () => (useCohorts ? completionCountsFromCohorts(cohorts, drill) : completionCounts(scopedEnrollments)),
    [useCohorts, cohorts, drill, scopedEnrollments]
  );
  const twoRing = React.useMemo(
    () =>
      useCohorts
        ? twoRingCompletionFromCohorts(cohorts, drill)
        : twoRingCompletion(enrollmentList, userIndex, drill),
    [useCohorts, cohorts, drill, enrollmentList, userIndex]
  );

  // Aggregate-by-cohort completion table (no individual rows — Req 4). Grouped one level below the drill
  // (see groupKeyFor); summed from the server cohorts when served, else from the enrollment rows.
  type CohortRow = { group: string; total: number; notStarted: number; inProgress: number; completed: number };
  const cohortRows = React.useMemo<CohortRow[]>(() => {
    const byGroup = new Map<string, CohortRow>();
    const ensure = (group: string): CohortRow => {
      let r = byGroup.get(group);
      if (!r) {
        r = { group, total: 0, notStarted: 0, inProgress: 0, completed: 0 };
        byGroup.set(group, r);
      }
      return r;
    };
    if (useCohorts) {
      for (const co of cohortsInDrill(cohorts, drill)) {
        const r = ensure(groupKeyFor(cohortSalesLevel(co), drill));
        r.total += co.enrolled;
        r.notStarted += co.not_started;
        r.inProgress += co.in_progress;
        r.completed += co.completed;
      }
    } else {
      const byGroupEnrollments = new Map<string, CampaignEnrollment[]>();
      for (const e of scopedEnrollments) {
        const key = groupKeyFor(enrollmentSalesLevel(userIndex, e), drill);
        const list = byGroupEnrollments.get(key) ?? [];
        list.push(e);
        byGroupEnrollments.set(key, list);
      }
      for (const [group, list] of byGroupEnrollments.entries()) {
        Object.assign(ensure(group), { group, ...completionCounts(list) });
      }
    }
    return Array.from(byGroup.values()).sort((a, b) => {
      if (a.group === 'Unassigned') return 1;
      if (b.group === 'Unassigned') return -1;
      return a.group.localeCompare(b.group);
    });
  }, [useCohorts, cohorts, scopedEnrollments, userIndex, drill]);

  // --- Req 2 + 5: deliveries scoped to the drill (by owner territory) ---
  const scopedDeliveries = React.useMemo(
    () => deliveriesInDrill(deliveries, userIndex, drill),
    [deliveries, userIndex, drill]
  );
  const seq = React.useMemo(() => deliverySequence(stepList), [stepList]);
  // Stacked delivery progression: one bar per sales-level cohort (one level below the drill), stacked by
  // progression stage. The builder applies the drill filter itself, so it takes the full delivery list.
  const progress = React.useMemo(
    () => farthestProgressByCohort(deliveries, seq, statusesByDelivery, userIndex, drill),
    [deliveries, seq, statusesByDelivery, userIndex, drill]
  );

  // Whole-integer left-axis ticks (deliveries are counts) sized to the tallest stacked column.
  const progressTicks = React.useMemo(() => {
    const n = progress.maxTotal;
    if (n <= 0) return [0];
    const step = Math.max(1, Math.ceil(n / 8));
    const ticks: number[] = [];
    for (let v = 0; v <= n; v += step) ticks.push(v);
    if (ticks[ticks.length - 1] !== n) ticks.push(n);
    return ticks;
  }, [progress.maxTotal]);

  // App-owned shaping (data contract + integer ticks); an admin may override only the appearance via a
  // persisted custom tile (Req 6) — see useCampaignTileAppearance.
  const progressFallback = React.useMemo<TileAppearance>(
    () => ({
      chartType: 'vertical_bar_chart',
      settings: {
        labelField: 'label',
        groupMode: 'stacked',
        layout: 'vertical',
        keys: progress.stages,
        valueFormat: 'number',
        axisLeftTickValues: progressTicks,
        // Legend off by request — with many progression stages the color key wrapped and overlapped
        // the axis labels. The stage split still reads from the stacked segments + tooltip.
        show_legend: false,
        axisLeftLegend: 'Deliveries',
        axisBottomLegend: groupLabel,
        // Gridlines off by request — the whole-integer ticks carry the scale on their own.
        enableGridX: false,
        enableGridY: false,
        // Tooltip keyed to the stacked segment's real dimensions: which stage, which group, how many.
        tooltipMode: 'seriesLabelValue',
        tooltipSeriesTitle: 'Progression stage',
        tooltipLabelTitle: groupLabel,
        tooltipValueTitle: 'Deliveries',
      },
    }),
    [progress.stages, progressTicks, groupLabel]
  );
  const progressAppearance = useCampaignTileAppearance(
    CampaignChartSlug.deliveryProgression,
    progressFallback
  );

  // --- Req 3 + 5 combined: pipeline $ + counts bucketed by the Webex workshop status (Not Scheduled /
  // Scheduled / In-Progress / Complete) — one surface tying session lifecycle to the pipeline it builds. ---
  const pipelineRows = React.useMemo(
    () => workshopPipelineRows(scopedDeliveries, meetingsByDelivery, targetsByDelivery, amountByOpp),
    [scopedDeliveries, meetingsByDelivery, targetsByDelivery, amountByOpp]
  );

  // Total attributed pipeline $ across the scoped deliveries — the sum of the same per-status rows the
  // "Pipeline by workshop status" table shows, surfaced as a headline KPI. Bounded by delivery targets
  // (not enrollments), so this stays a client-side sum with no scale concern.
  const totalPipeline = React.useMemo(
    () => pipelineRows.reduce((sum, r) => sum + r.amount, 0),
    [pipelineRows]
  );

  const kpis: MetricRow[] = [
    { label: 'Enrolled', value: enablementCounts.total },
    { label: 'In Progress', value: enablementCounts.inProgress },
    { label: 'Completed', value: enablementCounts.completed },
    { label: 'Deliveries', value: scopedDeliveries.length },
    { label: 'Total pipeline $', value: totalPipeline, display: formatPipelineUsd(totalPipeline) },
  ];

  const cohortCols: Column<CohortRow>[] = [
    { key: 'group', header: groupLabel, render: (r) => r.group },
    { key: 'total', header: 'Enrolled', numeric: true, render: (r) => r.total },
    { key: 'inProgress', header: 'In Progress', numeric: true, render: (r) => r.inProgress },
    { key: 'completed', header: 'Completed', numeric: true, render: (r) => r.completed },
  ];

  type PipeRow = (typeof pipelineRows)[number];
  const pipelineCols: Column<PipeRow>[] = [
    {
      key: 'status',
      header: 'Status',
      render: (r) => <StatusPill tone={toneForStatus(r.status)}>{r.label}</StatusPill>,
    },
    { key: 'count', header: 'Sessions', numeric: true, render: (r) => r.count },
    { key: 'noOpp', header: 'No opp', numeric: true, render: (r) => r.noOppCount },
    { key: 'amount', header: 'Attributed $', numeric: true, render: (r) => formatPipelineUsd(r.amount) },
  ];

  const scopeNote =
    drill.sl3 != null
      ? `${drill.sl1} · ${drill.sl2} · ${drill.sl3}`
      : drill.sl2 != null
      ? `${drill.sl1} · ${drill.sl2}`
      : drill.sl1 != null
      ? `${drill.sl1} (all sub-regions)`
      : 'All territories';

  return (
    <>
      {/* Fallback fan-out — only mounted where the Go rollup route is not served; each headless
          loader collects one delivery's step statuses for the campaign-wide charts. */}
      {useFanout &&
        deliveries.map((d) => (
          <DeliveryStepStatusLoader key={d.id} deliveryId={d.id} onLoad={handleLoad} />
        ))}

      {/* Top control bar — the sales-level drill scopes every surface below (aggregate only, no
          individual data). */}
      <SalesLevelDrillSelect pairs={drillPairs} drill={drill} onChange={setDrill} />

      <KpiStrip metrics={kpis} />

      {/* Metric cards above; the two charts sit side by side (completion mix + delivery progression),
          legend disabled so the stage key can't overlap the axis labels. */}
      <div className={css.grid}>
        <Panel
          title="Enablement completion"
          subtitle={`Nested: ${groupLabel}, each split by status · scope: ${scopeNote}.`}
        >
          <TwoRingDonut
            inner={twoRing.inner}
            outer={twoRing.outer}
            groupDimensionLabel={groupLabel}
            tileKey={`campaign-${campaign.id}-completion`}
            empty="No enrollments in this territory."
          />
        </Panel>
        <ChartCard
          title={`Delivery progression by ${groupLabel}`}
          chartType={progressAppearance.chartType}
          data={progress.rows}
          settings={progressAppearance.settings}
          fill
          tileKey={`campaign-${campaign.id}-farthest`}
        />
      </div>

      <div className={css.grid}>
        <Panel title={`Enablement by ${groupLabel}`} subtitle="Aggregate cohort completion" count={cohortRows.length} flush>
          <MasterTable
            columns={cohortCols}
            rows={cohortRows}
            getRowId={(r) => r.group}
            empty="No enrollments in this territory."
          />
        </Panel>
        <Panel title="Pipeline by workshop status" subtitle="Sessions + attributed $ by Webex schedule status, incl. sessions with no opportunity" flush>
          <MasterTable
            columns={pipelineCols}
            rows={pipelineRows}
            getRowId={(r) => r.status}
            empty="No deliveries in this territory."
          />
        </Panel>
      </div>
    </>
  );
};

export default CampaignView;
